import React, { useState } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { Formik } from 'formik';

import { BoxContainer, BoxItemTitle } from '../../../components';

import { RequiredFieldsForm } from '../../requiredFields/components';
import { useNormalizeRequiredFields } from '../../requiredFields/hooks';
import { useActiveAccount } from '../../../shared/activeAccount';

export const GET_PRODUCT_REQUIRED_INSURED_FIELDS = gql`
  query GetProductRequiredInsuredFields($id: ID!) {
    product(id: $id) {
      id
      insuranceType {
        id
        type
      }
      requiredInsuredFields
    }
  }
`;

const UPDATE_PRODUCT_REQUIRED_INSURED_FIELDS = gql`
  mutation updateProductRequiredInsuredFields($id: ID!, $requiredInsuredFields: [String]!) {
    updateProductRequiredInsuredFields(id: $id, requiredInsuredFields: $requiredInsuredFields) {
      id
      requiredInsuredFields
    }
  }
`;

export default function InsuredRequiredFields({
  productId,
  stateFees,
  roleBelowCollaborator,
  typeBelowBroker,
  onError = () => {}
}) {
  const {
    roleIsAtLeast,
    ROLES: { COLLABORATOR }
  } = useActiveAccount();
  const [success, setSuccess] = useState(false);

  const { data, loading, error } = useQuery(GET_PRODUCT_REQUIRED_INSURED_FIELDS, {
    variables: { id: productId }
  });

  const [update, { loading: updating, error: updateError }] = useMutation(UPDATE_PRODUCT_REQUIRED_INSURED_FIELDS, {
    onCompleted: () => {
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    },
    onError,
    refetchQueries: [
      {
        query: GET_PRODUCT_REQUIRED_INSURED_FIELDS,
        variables: { id: productId }
      }
    ]
  });

  const handleSubmit = values => {
    update({
      variables: {
        id: productId,
        requiredInsuredFields: values?.requiredFields
          ?.filter(field => field.required || field.alwaysRequired)
          ?.map(field => field.name)
      }
    });
  };

  const getFields = product => {
    if (product?.insuranceType?.type === 'INSURED') {
      return product?.requiredInsuredFields;
    } else {
      const keys = product?.requiredInsuredFields ? Object.keys(product?.requiredInsuredFields) : [];
      return keys.reduce((acc, key) => {
        if (key.includes('beneficiary')) return acc;
        return { ...acc, [key]: product?.requiredInsuredFields[key] };
      }, {});
    }
  };

  const normalizedInsuredFields = useNormalizeRequiredFields({
    fields: getFields(data?.product),
    stateFees
  });

  const initialValues = {
    requiredFields: normalizedInsuredFields
  };

  return (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
      {formik => (
        <BoxContainer className="p-3 mb-4">
          <BoxItemTitle
            title="Create Insured"
            subtitle="If enabled, these fields will be required to create a new Insured."
          />
          <RequiredFieldsForm
            formik={formik}
            loading={loading}
            updating={updating}
            error={error || updateError}
            success={success}
            showSubmitButton={roleIsAtLeast(COLLABORATOR)}
            disableAllFields={roleBelowCollaborator || typeBelowBroker}
          />
        </BoxContainer>
      )}
    </Formik>
  );
}
